import React, { useMemo } from 'react';

import { CloudProviders } from '@ariksa/cloud-account/api';
import { NativeResources } from '@ariksa/inventory-core';
import { Box } from '@chakra-ui/react';
import { customTheme } from 'theme';

import { CustomTooltip } from 'components/DataDisplay';
import {
  AttributeIconButton,
  DeleteAttributeIconButton,
  Select,
} from 'components/DataEntry';
import { OverviewIcon } from 'components/Icons';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { annotateBlockMaskOptions } from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/maskOptions';
import { snowflakeConditionGridStyles } from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/styles';

interface Props {
  condition: Record<string, any>;
  state: Record<string, any>;
  roleOptions?: Record<string, any>[];
  updateConditions: (
    item: Record<string, any>,
    key: string,
    value: any,
  ) => void;
  onDeleteCondition: (item: Record<string, any>, key: string) => void;
  isDeleteDisabled?: boolean;
}

export const SnowflakeCondition = (props: Props) => {
  const {
    condition,
    state,
    roleOptions,
    updateConditions,
    onDeleteCondition,
    isDeleteDisabled,
  } = props;
  const { onOpenMetadata } = useActiveResourceContext();
  const { updateActiveResource } = useActiveResourceActions();
  // These options are populated from Insight V2's SnowflakeRoles response.
  const resourceOptions =
    roleOptions ?? state?.resourceOptions?.SnowflakeRoles ?? [];
  const role = condition?.role;
  const roleResource = role?.data ?? role;
  const maskOptions = useMemo(
    () =>
      annotateBlockMaskOptions(state?.maskingOptions, state?.defaultMask?.value),
    [state?.maskingOptions, state?.defaultMask?.value],
  );
  // Resolve by value so the selected mask picks up the "(same as default)"
  // label after the default changes, without rewriting the stored condition.
  const selectedMask =
    maskOptions.find(option => option?.value === condition?.mask?.value) ??
    condition?.mask;

  const openRoleDetails = () => {
    updateActiveResource({
      resourceType: NativeResources.SnowflakeRoles,
      uuid: roleResource?.uuid,
      resourceId: roleResource?.resource_id,
      accountId: roleResource?.account,
      cloudProvider: CloudProviders.Snowflake,
    });
    onOpenMetadata();
  };

  return (
    <Box {...snowflakeConditionGridStyles}>
      <Box minW={0}>
        <Select
          options={resourceOptions}
          value={role}
          showClearIndicator={false}
          onChange={val => {
            updateConditions(condition, 'role', val);
          }}
          showIconInValueContainer
          valueContainerIcon={role?.icon}
          menuPortalTarget={document.body}
        />
      </Box>
      <Box minW={0}>
        <Select
          options={maskOptions}
          value={selectedMask}
          onChange={val => {
            updateConditions(condition, 'mask', val);
          }}
          showClearIndicator={false}
          menuPortalTarget={document.body}
        />
      </Box>
      <CustomTooltip label="Show details">
        <AttributeIconButton
          icon={<OverviewIcon />}
          color={customTheme.colors.gray['250']}
          onClick={openRoleDetails}
          isDisabled={!role?.value}
        />
      </CustomTooltip>
      <DeleteAttributeIconButton
        onClick={() => onDeleteCondition(condition, 'conditions')}
        isDisabled={isDeleteDisabled}
      />
    </Box>
  );
};
